// ABOUTME: Card component displaying a daily outfit suggestion
// ABOUTME: Shows item thumbnails, match score, and accept/skip actions

import { CheckIcon, StarIcon, SwatchIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useGameStore } from '@/lib/store/gameStore'
import type { SuggestedOutfit } from '@/lib/store/suggestionStore'
import { getPersonalizationLabel } from '@/lib/utils/personalization'

interface DailySuggestionCardProps {
  suggestion: SuggestedOutfit
  index: number
  onAccept: () => void
  onSkip: () => void
}

export default function DailySuggestionCard({
  suggestion,
  index,
  onAccept,
  onSkip,
}: DailySuggestionCardProps) {
  const { colorProfile, onboarding } = useGameStore()
  const label = getPersonalizationLabel(colorProfile, onboarding)

  return (
    <div className="bg-surface rounded-lg shadow-glass p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-fg-primary">Suggestion #{index + 1}</h3>
        <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
          <StarIcon className="w-3 h-3" />
          {suggestion.score}%
        </span>
      </div>

      {label && (
        <p className="flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 mb-3">
          <SwatchIcon className="w-3 h-3" />
          {label}
        </p>
      )}

      <div className="flex gap-2 mb-3">
        {suggestion.items.map((item) => (
          <div key={item.id} className="flex-1 min-w-0">
            <img
              src={item.thumbnailData}
              alt={item.name}
              className="w-full aspect-square object-cover rounded-lg"
            />
            <p className="text-xs text-fg-primary truncate mt-1">{item.name}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-fg-secondary mb-3">{suggestion.description}</p>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onAccept}
          className="flex-1 btn-primary text-sm py-2 inline-flex items-center justify-center gap-1"
        >
          <CheckIcon className="w-4 h-4" />
          Wear This
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="px-4 py-2 text-sm bg-surface rounded-lg text-fg-secondary hover:bg-surface-elevated inline-flex items-center gap-1"
        >
          <XMarkIcon className="w-4 h-4" />
          Skip
        </button>
      </div>
    </div>
  )
}
